import type { Proposal } from '../../type/types';
import {
    QUOTE_COLUMNS,
    INVOICE_COLUMNS,
    PDF_QUOTE_COLUMNS,
    PDF_INVOICE_COLUMNS,
} from '../../constants';
import { cn } from '@rocco/index';
import { PROPOSAL_TYPO_CLASS } from './proposal-styles';
import PaymentRow from './PaymentRow';

interface ScheduledPaymentsSectionProps {
    proposal: Proposal;
    isMobile: boolean;
    isPdf?: boolean;
    onPayNowClick: (paymentId: number) => void;
    hasPayButton: boolean;
    unpaidPayment: Proposal['paymentItems'][number] | undefined;
}

/**
 * Renders the Scheduled Payments section with header and payment rows.
 */
export const ScheduledPaymentsSection = ({
    proposal,
    isMobile,
    isPdf = false,
    onPayNowClick,
    hasPayButton,
    unpaidPayment,
}: ScheduledPaymentsSectionProps) => {
    const isQuote = proposal.docType === 'quote';
    const columns = isPdf
        ? isQuote
            ? PDF_QUOTE_COLUMNS
            : PDF_INVOICE_COLUMNS
        : isQuote
          ? QUOTE_COLUMNS
          : INVOICE_COLUMNS;

    return (
        <div className="space-y-2 pt-[1rem] md:space-y-1 md:pt-[1.8rem] print:break-inside-avoid print:space-y-1 print:pt-[1.8rem]">
            {/* Header */}
            <div className="mb-[0.9rem] flex items-end md:mb-[0.83rem] md:h-[1.17rem] print:mb-[0.83rem] print:h-[1.17rem]">
                {columns.map((col, index) => {
                    if (index === 0) {
                        return (
                            <div
                                key={col.key}
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_HEADER_TITLE,
                                    'flex-1 whitespace-nowrap pl-[0.13rem] md:pb-[0.13rem] md:pl-[0.13rem]',
                                )}
                            >
                                {col.header}
                            </div>
                        );
                    }
                    if (isMobile) return null;
                    if (index === 1) return null;
                    return (
                        <div
                            key={col.key}
                            className={cn(
                                col.header &&
                                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_HEADER,
                                'md:pr-[1rem] print:pr-[1rem]',
                                col.className,
                                col.header && 'whitespace-nowrap',
                            )}
                        >
                            {col.header}
                        </div>
                    );
                })}
            </div>

            {proposal.paymentItems?.map((paymentItem) => (
                <PaymentRow
                    paymentItem={paymentItem}
                    currency={proposal.currency}
                    key={paymentItem.id}
                    proposal={proposal}
                    onPayNowClick={onPayNowClick}
                    enablePayButton={
                        hasPayButton && paymentItem.id === unpaidPayment?.id
                    }
                    isMobile={isMobile}
                    isPdf={isPdf}
                />
            )) || []}
        </div>
    );
};
